import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createContext } from '@shared/utils/createContext';
import type { ShippingAddressFragment } from '@graphql/aliases';
import type { RegistryItemFragment } from '@graphql/fragments/registry.graphql.generated';
import { useDisclosure } from '@withjoy/joykit';
import { AddressReminderDialog } from '@apps/registry/common/components';
import { CurrencyDialog } from '@apps/registry/common/components/CurrencyDialog';
import type { ShippingAddressFormFragment } from '@shared/components/ShippingAddress/ShippingAddress.types';
import { LazyShippingAddressReminderDialog } from '@shared/components/ShippingAddress/ShippingAddress.lazy';
import { toShippingAddressFormFragment } from '@shared/components/ShippingAddress/ShippingAddress.utils';
import type { DateFragment } from '@graphql/fragments/coreTypeDefs.graphql.generated';
import type { EventRegistryCurrencyFragment, RegistryCurrencyCodesFragment } from '@graphql/fragments/currency.graphql.generated';
import { useAddCustomGiftDialogContext } from '../AddCustomGiftDialog/AddCustomGiftDialog.provider';
import { useInstallBrowserButtonProps } from '@apps/registry/admin/hooks/useInstallBrowserButton';
import { ShopCurrencyWarningDialog } from '../Catalog/components/ShopCurrencyWarningDialog';
import { useIsOutsideUsDeliveryRange } from '@apps/registry/common/hooks/useIsOutsideUsDeliveryRange';
import { isUSAddress } from '@apps/registry/common/utils/shippingAddressValidations';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useMount } from '@shared/utils/hooks/useMount';
import type { AdminRegistryList, RegistryOrderList, RegistryList } from '../../state/RegistryProducts';
import { ShippingAddressTelemetryProvider, useShippingAddressTelemetry } from './ShippingAddress.telemetry';
import { LazyAddressDialogShippingContent } from './AddressDialogShippingContent.lazy';
import { triggerDrawerOpen } from '../Catalog/components/RecentlyAddedRegistryItemUpdateDrawer/recentlyAddedDrawerTrigger';

const ADDRESS_SAVE_REFETCH_QUERIES = ['GetAdminRegistryData', 'GetRegistryOnboardingSuggestions', 'PublicShopRoot'];

type FuncArgType = () => void;

export type GeneralDialogsDriverContext = {
  isAddressDialogOpen: boolean;
  openAddressDialog: (from: string) => void;
  closeAddressDialog: () => void;

  isAddressReminderDialogOpen: boolean;
  openAddressReminderDialog: () => void;
  closeAddressReminderDialog: () => void;

  /**
   * Opens the reminder for an add that has already completed. The item exists, so
   * instead of deferring the add itself this queues "show the drawer for this item",
   * which every exit from the address flow already runs — and which replaces any
   * callback left by an earlier, abandoned add rather than replaying it.
   */
  showAddressReminderForCompletedAdd: (item: RegistryItemFragment) => void;

  isCurrencyDialogOpen: boolean;
  openCurrencyDialog: () => void;
  closeCurrencyDialog: () => void;
  registryShippingAddress: ShippingAddressFragment | null;

  showOutOfUSAWarningOrAddItem: (funcArg: FuncArgType) => void;

  /**
   * `true` once the registry has saved a shipping address Joy cannot deliver to.
   * Joy-sponsored checkout (Add to Cart / Buy Now) is US-only, so purchase surfaces
   * render an unavailable state instead of a checkout that can only fail.
   *
   * Anything saved that is not a US address counts, including the free-text "Other"
   * country option, which persists a country with no `countryCode`. Unlike
   * `isOutsideUsDeliveryRange` this never falls back to the browser's geo locale:
   * a registry with no address yet still gets the address reminder, not a dead button.
   */
  isJoyCheckoutUnavailable: boolean;

  /**
   * `true` when the next deliverable add will open the `AddressReminderDialog`
   * (i.e. the registry has no shipping address yet). Add surfaces use this to
   * avoid eagerly opening competing UI (e.g. the recently-added drawer) that
   * would occlude the reminder.
   */
  willShowAddressReminderOnAdd: boolean;

  /**
   * View this state chart to view the expected states
   * https://stately.ai/viz/416e6d06-9f0b-4c39-86d2-5d165275711c
   *
   * If no address and product type is not cash or charity:
   *  - Show address reminder dialog
   *    - if "add later", add catalog item
   *    - Open address dialog
   *
   * If address:
   *   - If domestic address, add catalog item
   *   - If international address, show currency warning dialog
   */
  verifyAddressAndTryAddCatalogItem: (funcArg: FuncArgType) => void;
};

const [Provider, useShippingAddressAndCurrencyDialogsContext] = createContext<GeneralDialogsDriverContext>({ name: 'GeneralDialogsProvider' });

interface GeneralDialogsDriverProviderProps
  extends Readonly<{
    eventId: string;
    registryShippingAddress: ShippingAddressFragment | null;
    registryCurrency: Maybe<EventRegistryCurrencyFragment>;
    onSetRegistryCurrency?: (payload: RegistryCurrencyCodesFragment) => void;
    finalizedEventDate: Maybe<DateFragment>;
    orders?: RegistryOrderList;
    registries?: AdminRegistryList | RegistryList | null;
    website?: string | null;
  }> {}

const useDeferredAddCatalogItemCallback = () => {
  // Opting to use ref so that we can consistently keep it up to date
  const deferredAddCatalogItemCallbackRef = useRef<FuncArgType | null>(null);

  const clearDeferredAddCatalogItemCb = useCallback(() => {
    deferredAddCatalogItemCallbackRef.current = null;
  }, []);

  const deferAddCatalogItemCb = useCallback((cb: FuncArgType) => {
    deferredAddCatalogItemCallbackRef.current = cb;
  }, []);

  const executeDeferredAddCatalogItemCb = useCallback(() => {
    deferredAddCatalogItemCallbackRef.current?.();
    clearDeferredAddCatalogItemCb();
  }, [clearDeferredAddCatalogItemCb]);

  const checkHasDeferredAddCatalogItemCb = useCallback(() => {
    return !!deferredAddCatalogItemCallbackRef.current;
  }, []);

  return {
    /**
     * Stable utility function to check if there is a callback currently queued.
     */
    checkHasDeferredAddCatalogItemCb,
    /**
     * Flush any stored callback.
     */
    clearDeferredAddCatalogItemCb,
    /**
     * Collect the callback for later usage.
     */
    deferAddCatalogItemCb,
    /**
     * Will execute any outstanding add catalog item callbacks, if any.
     */
    executeDeferredAddCatalogItemCb,
  };
};

/**
 * Must be a child of `AddCustomGiftDialogProvider`.
 *
 * Handles:
 * - CurrencyDialog
 * - AddressReminderDialog
 * - ShippingAddressReminderDialog (shipping address form)
 * - ShopCurrencyWarningDialog
 */

export const ShippingAddressAndCurrencyDialogsProvider: React.FC<GeneralDialogsDriverProviderProps> = props => {
  const { eventId, children, onSetRegistryCurrency, registryShippingAddress, registryCurrency, website, finalizedEventDate, registries, orders } = props;

  const { isOpen: isAddressDialogOpen, onOpen: openAddressDialog, onClose: closeAddressDialog } = useDisclosure();
  const { isOpen: isCurrencyDialogOpen, onOpen: openCurrencyDialog, onClose: closeCurrencyDialog } = useDisclosure();
  const { isOpen: isCurrencyWarningDialogOpen, onOpen: openCurrencyWarningDialog, onClose: closeCurrencyWarningDialog } = useDisclosure();
  const { isOpen: isAddressReminderDialogOpen, onOpen: openAddressReminderDialog, onClose: closeAddressReminderDialog } = useDisclosure();

  const { clearDeferredAddCatalogItemCb, deferAddCatalogItemCb, checkHasDeferredAddCatalogItemCb, executeDeferredAddCatalogItemCb } = useDeferredAddCatalogItemCallback();
  const { openModal: openAddCustomGiftDialog } = useAddCustomGiftDialogContext();
  const [addressDialogTrigger, setAddressDialogTrigger] = useState<string>('');
  // Remind on every deliverable add until the couple sets a shipping address.
  const willShowAddressReminderOnAdd = !registryShippingAddress;

  const handleOnAddCustomGiftClick = useCallback(() => {
    clearDeferredAddCatalogItemCb();
    openAddCustomGiftDialog();
  }, [clearDeferredAddCatalogItemCb, openAddCustomGiftDialog]);

  const onOpenAddressDialog = useCallback(
    (from: string) => {
      setAddressDialogTrigger(from);
      openAddressDialog();
    },
    [openAddressDialog, setAddressDialogTrigger],
  );

  const isOutsideUsDeliveryRange = useIsOutsideUsDeliveryRange(registryShippingAddress);
  const isJoyCheckoutUnavailable = !!registryShippingAddress && !isUSAddress(registryShippingAddress);

  const showOutOfUSAWarningOrAddItem = useCallback(
    (cb: () => void) => {
      if (isOutsideUsDeliveryRange) {
        openCurrencyWarningDialog();
      } else {
        cb();
      }
    },
    [isOutsideUsDeliveryRange, openCurrencyWarningDialog],
  );

  const wrappedShowOutOfUSAWarningOrAddItem = useCallback(() => {
    showOutOfUSAWarningOrAddItem(executeDeferredAddCatalogItemCb);
  }, [showOutOfUSAWarningOrAddItem, executeDeferredAddCatalogItemCb]);

  // Opens the recently-added drawer (suppressed while the reminder was shown)
  // and then runs the deferred add. Kept together so the drawer never opens
  // ahead of the actual add — e.g. when the currency warning intervenes first.
  const openRecentlyAddedDrawerThenAddItem = useCallback(() => {
    triggerDrawerOpen();
    executeDeferredAddCatalogItemCb();
  }, [executeDeferredAddCatalogItemCb]);

  const showAddressReminderForCompletedAdd = useCallback(
    (item: RegistryItemFragment) => {
      deferAddCatalogItemCb(() => triggerDrawerOpen(item));
      openAddressReminderDialog();
    },
    [deferAddCatalogItemCb, openAddressReminderDialog],
  );

  const verifyAddressAndTryAddCatalogItem = useCallback(
    (addCatalogItemCb: () => void) => {
      // Store add catalog item callback to be called when appropriate
      deferAddCatalogItemCb(addCatalogItemCb);
      if (registryShippingAddress) {
        // Must verify shipping address before adding catalog item
        wrappedShowOutOfUSAWarningOrAddItem();
      } else {
        // No shipping address yet — prompt the couple to add one.
        openAddressReminderDialog();
      }
    },
    [deferAddCatalogItemCb, wrappedShowOutOfUSAWarningOrAddItem, openAddressReminderDialog, registryShippingAddress],
  );

  const ctx = useMemo<GeneralDialogsDriverContext>(() => {
    return {
      closeAddressDialog,
      closeAddressReminderDialog,
      closeCurrencyDialog,
      isAddressDialogOpen,
      isAddressReminderDialogOpen,
      isCurrencyDialogOpen,
      openAddressReminderDialog,
      showAddressReminderForCompletedAdd,
      openAddressDialog: onOpenAddressDialog,
      openCurrencyDialog,
      showOutOfUSAWarningOrAddItem,
      isJoyCheckoutUnavailable,
      registryShippingAddress,
      willShowAddressReminderOnAdd,
      verifyAddressAndTryAddCatalogItem,
    };
  }, [
    closeAddressReminderDialog,
    closeAddressDialog,
    closeCurrencyDialog,
    isAddressDialogOpen,
    isAddressReminderDialogOpen,
    isCurrencyDialogOpen,
    openAddressReminderDialog,
    showAddressReminderForCompletedAdd,
    onOpenAddressDialog,
    openCurrencyDialog,
    registryShippingAddress,
    willShowAddressReminderOnAdd,
    showOutOfUSAWarningOrAddItem,
    isJoyCheckoutUnavailable,
    verifyAddressAndTryAddCatalogItem,
  ]);

  //-----------------------------------
  // Address Reminder Dialog callbacks
  const handleOnAddressReminderActionClick = useCallback(() => {
    closeAddressReminderDialog();
    onOpenAddressDialog('AddressReminderDialog');
  }, [closeAddressReminderDialog, onOpenAddressDialog]);

  /**
   * Closing the AddressReminderDialog directly will clear out any deferred
   * add catalog item callback.
   */
  const handleOnCloseAddressReminderDialog = useCallback(() => {
    // The eager recently-added drawer was suppressed while the reminder was
    // shown; surface it now that the gift is actually being added.
    openRecentlyAddedDrawerThenAddItem();
    clearDeferredAddCatalogItemCb();
    closeAddressReminderDialog();
  }, [closeAddressReminderDialog, clearDeferredAddCatalogItemCb, openRecentlyAddedDrawerThenAddItem]);

  //-----------------------------------
  // Address Dialog callbacks
  const handleOnCloseAddressDialog = useCallback(() => {
    if (addressDialogTrigger === 'AddressReminderDialog') {
      openRecentlyAddedDrawerThenAddItem();
    } else {
      executeDeferredAddCatalogItemCb();
    }
    clearDeferredAddCatalogItemCb();
    closeAddressDialog();
  }, [addressDialogTrigger, clearDeferredAddCatalogItemCb, closeAddressDialog, executeDeferredAddCatalogItemCb, openRecentlyAddedDrawerThenAddItem]);

  const handleOnSaveAddress = useCallback(() => {
    closeAddressDialog();
    if (checkHasDeferredAddCatalogItemCb()) {
      if (addressDialogTrigger === 'AddressReminderDialog') {
        // Open the recently-added drawer only when the add actually runs — not
        // if the currency warning intervenes first. registryShippingAddress is
        // still the pre-save value in this closure, so a non-US browser locale
        // can route through the warning instead of adding immediately.
        showOutOfUSAWarningOrAddItem(openRecentlyAddedDrawerThenAddItem);
      } else {
        wrappedShowOutOfUSAWarningOrAddItem();
      }
    }
  }, [
    addressDialogTrigger,
    closeAddressDialog,
    showOutOfUSAWarningOrAddItem,
    openRecentlyAddedDrawerThenAddItem,
    wrappedShowOutOfUSAWarningOrAddItem,
    checkHasDeferredAddCatalogItemCb,
  ]);

  const { onClick: showInstallBrowserButton } = useInstallBrowserButtonProps({ eventId, website });

  const { eventHandle: eventInfoEventHandle } = useEventInfo();
  const eventHandle = eventInfoEventHandle || website || '';

  const dialogShippingAddress = useMemo(() => toShippingAddressFormFragment(registryShippingAddress), [registryShippingAddress]);

  return (
    <Provider value={ctx}>
      <ShippingAddressTelemetryProvider>
        {children}
        <CurrencyDialog
          isOpen={isCurrencyDialogOpen}
          onClose={closeCurrencyDialog}
          eventId={eventId}
          registryCurrency={registryCurrency}
          setRegistryCurrency={onSetRegistryCurrency}
        />
        <ShopCurrencyWarningDialog
          isOpen={isCurrencyWarningDialogOpen}
          onClose={closeCurrencyWarningDialog}
          eventId={eventId}
          onAddAnyway={executeDeferredAddCatalogItemCb}
          onAddGift={handleOnAddCustomGiftClick}
          showInstallBrowserButton={showInstallBrowserButton}
        />
        <AddressReminderDialog
          isOpen={isAddressReminderDialogOpen}
          eventId={eventId}
          onClose={handleOnCloseAddressReminderDialog}
          onActionClick={handleOnAddressReminderActionClick}
          orders={orders}
          finalizedEventDate={finalizedEventDate}
          registries={registries}
        />
        {isAddressDialogOpen && (
          <RegistryAddressDialog
            eventId={eventId}
            eventHandle={eventHandle}
            trigger={addressDialogTrigger}
            shippingAddress={dialogShippingAddress}
            onClose={handleOnCloseAddressDialog}
            onSaved={handleOnSaveAddress}
          />
        )}
      </ShippingAddressTelemetryProvider>
    </Provider>
  );
};

type RegistryAddressDialogProps = Readonly<{
  eventId: string;
  eventHandle: string;
  trigger: string;
  shippingAddress?: ShippingAddressFormFragment;
  onClose: () => void;
  onSaved: () => void;
}>;

const RegistryAddressDialog: React.FC<RegistryAddressDialogProps> = ({ eventId, eventHandle, trigger, shippingAddress, onClose, onSaved }) => {
  const telemetry = useShippingAddressTelemetry({ addToContext: { eventId } });

  useMount(() => {
    telemetry.addressDialogOpened(trigger);
  });

  return (
    <LazyShippingAddressReminderDialog
      defaultView="form"
      eventId={eventId}
      eventHandle={eventHandle}
      isOpen
      onClose={onClose}
      onSaved={onSaved}
      pageName={trigger}
      shippingAddress={shippingAddress}
      additionalRefetchQueries={ADDRESS_SAVE_REFETCH_QUERIES}
      bottomContent={<LazyAddressDialogShippingContent eventId={eventId} />}
    />
  );
};

export {
  /**
   * Grants access to these dialogs:
   * - `<CurrencyDialog />`
   * - `<CurrencyWarningDialog />`
   * - `<AddressReminderDialog />`
   * - `<ShippingAddressReminderDialog />` (shipping address form)
   *
   * Also exposes utility method to defer adding a catalog item until the
   * shipping address is validated or user elects to add anyways.
   * - `verifyAddressAndTryAddCatalogItem()`
   */
  useShippingAddressAndCurrencyDialogsContext,
};
